import React, { useState } from 'react';
import { Box, Button, Heading, HStack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { ALERT_STATUS_TYPE, CARD_TYPE, ROLES } from '@constants';
import { setAlert } from '@store/actions/alertActions';
import { FaArrowRightLong } from 'react-icons/fa6';
import useAuthRole from '@hooks/useAuthRole';
import { useDispatch } from 'react-redux';
import ModalLayout from '@components/Modal/ModalLayout';
import ConfirmModal from '@components/Modal/ConfirmModal';
import { getFalconTransactionTable, getPayoutTable, getRecipientTable } from './utils';
import {
  forceFailTransaction,
  forceProcessTransaction,
  getForceStatusSync,
  updateOnHoldTxnStatus,
  setTransactionTerminalState,
  getFalconTransactionDetails
} from '@store/actions/falconDashboardActions';
import DetailsTable from '@components/DetailsTable';
import GenericCard from '@components/GenericCard';
import {
  MdOutlineBolt,
  MdOutlineErrorOutline,
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp
} from 'react-icons/md';
import { FALCON_STATUS } from '@views/FalconServiceDashboard/constant';
import { FALCON_DASHBOARD_CONSTANTS } from '@store/constants';
import ForceFailTransactionModal
  from '@components/FalconServiceDashboard/ForceFailTransactionModal/ForceFailTransactionModal';
import OnHoldTransactionUpdateModal
  from '@components/FalconServiceDashboard/OnHoldTransactionUpdateModal/OnHoldTransactionUpdateModal';
import ForceProcessTransactionModal
  from '@components/FalconServiceDashboard/ForceProcessTransactionModal/ForceProcessTransactionModal';


const FalconTransactionCard = ({
  onOrderDetailsClick,
  falconTransaction,
  recipient,
  payout,
  isSidebar = false,
  terminalState=false,
  configs,
  selected
}) => {
  const {
    isOpen: isConfirmSyncModalOpen,
    onOpen: onConfirmSyncModalOpen,
    onClose: onConfirmSyncModalClose
  } = useDisclosure();

  const {
    isOpen: isForceFailTransactionModalOpen,
    onOpen: onForceFailTransactionModalOpen,
    onClose: onForceFailTransactionModalClose
  } = useDisclosure();

  const {
    isOpen: isOnHoldHandlingModalOpen,
    onOpen: onOnHoldHandlingModalOpen,
    onClose: onOnHoldHandlingModalClose
  } = useDisclosure();

  const {
    isOpen: isForceProcessTransactionModalOpen,
    onOpen: onForceProcessTransactionModalOpen,
    onClose: onForceProcessTransactionModalClose
  } = useDisclosure();

  const {
    isOpen: isTerminalStateModalOpen,
    onOpen: onTerminalStateModalOpen,
    onClose: onTerminalStateModalClose
  } = useDisclosure();

  const dispatch = useDispatch();

  const { hasAnyAuthority } = useAuthRole();
  const [isTransactionCollapsed, setIsTransactionCollapsed] = useState(false);
  const [isRecipientCollapsed, setIsRecipientCollapsed] = useState(false);
  const [isPayoutCollapsed, setIsPayoutCollapsed] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const recipientObj = getRecipientTable(recipient) || null;
  const onHoldTransactionObj = getFalconTransactionTable(falconTransaction)
  const transactionObj = getFalconTransactionTable(falconTransaction);

  const canShowTerminalStateButton =
    falconTransaction?.status !== FALCON_STATUS.FAILED &&
    falconTransaction?.status !== FALCON_STATUS.VALIDATION_FAILED &&
    falconTransaction?.status !== FALCON_STATUS.EXPIRED;

  const handleForceStatusSync = ({ isConfirmed }) => {
    if (!isConfirmed) {
      onConfirmSyncModalOpen();
      return;
    }
    const latestPayoutId = getLatestPayoutId(); // must have one to be able to force sync or force fail
    setIsModalLoading(true);
    dispatch(
      getForceStatusSync({
        payoutId: latestPayoutId,
        onSuccess: (data) => {
          dispatch({
            type: FALCON_DASHBOARD_CONSTANTS.UPDATE_LAST_FALCON_TRANSACTION_PAYOUT_DETAILS,
            payload: data
          });
        },
        onComplete: () => {
          setIsModalLoading(false);
          onConfirmSyncModalClose();
        }
      })
    );
  };

  const handleUpdateStatusForOnHoldModal = ({data, modal=false }) => {
    
    if(modal){
      onOnHoldHandlingModalOpen()
      return
    }


    const transactionId = falconTransaction?.transactionId;
  
    if(data?.action === '' || data?.action === undefined){
      dispatch(
        setAlert({
          title: 'Action cannot be empty',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    setIsModalLoading(true);

    const payload = {
      transactionId: transactionId,
      action: data?.action,
      forceRoutingPartners: data?.forceRoutingPartners
    }

    dispatch(
      updateOnHoldTxnStatus({
        payload,
        onSuccess: (data) => {
          dispatch({
            type: FALCON_DASHBOARD_CONSTANTS.FORCE_UPDATE_ON_HOLD_TXN_STATUS,
            payload: data
          });
        },
        onComplete: () => {
          setIsModalLoading(false);
          onOnHoldHandlingModalClose();
        }
      })
    );
    
    
  }
  
  const handleForceFailTransaction = ({ data, isForceFail = false }) => {
    if (!isForceFail) {
      onForceFailTransactionModalOpen();
    } else {
      const latestPayoutId = getLatestPayoutId();
      if (data?.comment === '' || data?.comment === undefined) {
        dispatch(
          setAlert({
            title: 'comments cannot be empty',
            status: ALERT_STATUS_TYPE.ERROR
          })
        );
        return;
      }
      setIsModalLoading(true);
      const payload = {
        ...data,
        payoutId: latestPayoutId
      };
      dispatch(
        forceFailTransaction({
          payload,
          onSuccess: (data) => {
            dispatch({
              type: FALCON_DASHBOARD_CONSTANTS.UPDATE_LAST_FALCON_TRANSACTION_PAYOUT_DETAILS,
              payload: data
            });
          },
          onComplete: () => {
            setIsModalLoading(false);
            onForceFailTransactionModalClose();
          }
        })
      );
    }
  };

  const handleForceProcessTransaction = ({ data, isForceComplete = false }) => {
    if (!isForceComplete) {
      onForceProcessTransactionModalOpen();
    } else {
      setIsModalLoading(true);
      const latestPayoutId = getLatestPayoutId();
      const payload = {
        internalPayoutId: latestPayoutId || '',
        comment: data?.comment || '',
        externalPayoutId: data?.externalPayoutId || ''
      };

      dispatch(
        forceProcessTransaction({
          payload: payload,
          onSuccess: (data) => {
            dispatch({
              type: FALCON_DASHBOARD_CONSTANTS.UPDATE_LAST_FALCON_TRANSACTION_PAYOUT_DETAILS,
              payload: data
            });
          },
          onComplete: () => {
            setIsModalLoading(false);
            onForceProcessTransactionModalClose();
          }
        })
      );
    }
  };

  const handleSetTerminalState = ({ isConfirmed }) => {
    if (!isConfirmed) {
      onTerminalStateModalOpen();
      return;
    }
    const transactionId = falconTransaction?.transactionId;
    setIsModalLoading(true);
    // If terminalState is currently true (re-routing disabled), we want to set it to false (enable re-routing)
    // If terminalState is currently false (re-routing enabled), we want to set it to true (disable re-routing)
    const newTerminalState = !terminalState;
    dispatch(
      setTransactionTerminalState({
        payload: {
          transaction_id: transactionId,
          is_terminal_state: newTerminalState
        },
        onSuccess: () => {
          dispatch(
            getFalconTransactionDetails({
              selectedTransaction: falconTransaction,
              transactionId: transactionId,
              recipientId: recipient?.recipientId
            })
          );
        },
        onComplete: () => {
          setIsModalLoading(false);
          onTerminalStateModalClose();
        }
      })
    );
  };

  const getLatestPayoutName = () => {
    if (!payout || payout?.length === 0) return null;
    const latestPayout = payout[payout?.length - 1];
    return latestPayout?.payoutPartner;
  };

  const getLatestPayoutId = () => {
    if (!payout || payout?.length === 0) return null;
    const latestPayout = payout[payout?.length - 1];
    return latestPayout?.internalPayoutId;
  };

  return (
    <Box position="relative" width={!isSidebar ? '100%' : ''}>
      {configs && (
        <ModalLayout
          isOpen={isForceFailTransactionModalOpen}
          onClose={onForceFailTransactionModalClose}
          textAlign="center"
          size={'md'}
        >
          <ForceFailTransactionModal
            onSubmit={(data) => {
              handleForceFailTransaction({
                isForceFail: true,
                data
              });
            }}
            payoutPartnerName={getLatestPayoutName() || 'N/A'}
            onCancel={onForceFailTransactionModalClose}
            isLoading={isModalLoading}
            listOfAvailableClients={configs?.payoutPartners}
          />
        </ModalLayout>
      )}

      
        <ModalLayout
          isOpen={isOnHoldHandlingModalOpen}
          onClose={onOnHoldHandlingModalClose}
          textAlign="center"
          size={'md'}
        >
          <OnHoldTransactionUpdateModal
            onSubmit={(data) => {
              handleUpdateStatusForOnHoldModal({
                modal: false,
                data
              });
            }}
            payoutPartnerName={getLatestPayoutName() || 'N/A'}
            onCancel={onForceFailTransactionModalClose}
            isLoading={isModalLoading}
            listOfAvailableClients={configs?.payoutPartners}
          />
        </ModalLayout>
      

      <ModalLayout
        isOpen={isConfirmSyncModalOpen}
        onClose={() => {
          onConfirmSyncModalClose();
        }}
        textAlign="center"
        size={'md'}
      >
        <ConfirmModal
          handleSubmit={() => {
            handleForceStatusSync({ isConfirmed: true });
          }}
          prompt={'Are you sure you want to force sync this transaction ?'}
          isLoading={isModalLoading}
        />
      </ModalLayout>

      <ModalLayout
        isOpen={isTerminalStateModalOpen}
        onClose={() => {
          onTerminalStateModalClose();
        }}
        textAlign="center"
        size={'md'}
      >
        <ConfirmModal
          handleSubmit={() => {
            handleSetTerminalState({ isConfirmed: true });
          }}
          prompt={
            terminalState
              ? 'Are you sure you want to enable re-routing for this transaction?'
              : 'Are you sure you want to disable re-routing for this transaction?'
          }
          isLoading={isModalLoading}
        />
      </ModalLayout>

      {configs && (
        <ModalLayout
          isOpen={isForceProcessTransactionModalOpen}
          onClose={onForceProcessTransactionModalClose}
          textAlign="center"
          size={'md'}
        >
          <ForceProcessTransactionModal
            isLoading={isModalLoading}
            payoutPartnerName={getLatestPayoutName() || 'N/A'}
            onSubmit={(data) => {
              handleForceProcessTransaction({
                data,
                isForceComplete: true
              });
            }}
            onClose={() => {
              onForceProcessTransactionModalClose();
            }}
          />
        </ModalLayout>
      )}

      {isSidebar ? (
        <VStack
          overflowY={{ base: 'initial', lg: 'scroll' }}
          overflowX="hidden"
          alignItems="stretch"
          gap={4}
        >
          {/* Transaction Details */}
          <VStack
            w={'100%'}
            alignSelf={'start'}
            alignItems={'stretch'}
            justifyContent={'space-between'}
            border={'1px solid'}
            borderColor={'whiteAlpha.300'}
            bg={'backGround'}
            borderRadius={'xl'}
            p={4}
          >
            <HStack justifyContent="space-between">
              <Heading fontSize="lg" color="brand.500">
                Transaction Details
              </Heading>
              <Button
                size="sm"
                bg="black"
                border={'1px solid'}
                borderColor={'whiteAlpha.300'}
                borderRadius={'xl'}
                onClick={() =>
                  setIsTransactionCollapsed(!isTransactionCollapsed)
                }
                p={0}
                variant={'subtle'}
              >
                {isTransactionCollapsed ? (
                  <MdOutlineKeyboardArrowDown size={'1.25rem'} />
                ) : (
                  <MdOutlineKeyboardArrowUp size={'1.25rem'} />
                )}
              </Button>
            </HStack>
            {!isTransactionCollapsed && (
              <VStack
                w="100%"
                alignSelf="start"
                alignItems="stretch"
                justifyContent="space-between"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="xl"
                bg="blackAlpha.600"
                px={4}
                py={2}
              >
                {transactionObj.map(
                  (item, index) =>
                    item.data && (
                      <DetailsTable
                        key={index}
                        isSidebar={isSidebar}
                        details={item.data}
                        rowGap="0.55rem"
                        marginTop="0"
                      />
                    )
                )}
              </VStack>
            )}
          </VStack>

          {/* Recipient Details */}
          <VStack
            w={'100%'}
            alignSelf={'start'}
            alignItems={'stretch'}
            justifyContent={'space-between'}
            border={'1px solid'}
            borderColor={'whiteAlpha.300'}
            bg={'backGround'}
            borderRadius={'xl'}
            p={4}
          >
            <HStack justifyContent="space-between">
              <Heading fontSize="lg" color="brand.500">
                Recipient Details
              </Heading>
              <Button
                size="sm"
                bg="black"
                border={'1px solid'}
                borderColor={'whiteAlpha.300'}
                borderRadius={'xl'}
                onClick={() => setIsRecipientCollapsed(!isRecipientCollapsed)}
                p={0}
                variant={'subtle'}
              >
                {isRecipientCollapsed ? (
                  <MdOutlineKeyboardArrowDown size={'1.25rem'} />
                ) : (
                  <MdOutlineKeyboardArrowUp size={'1.25rem'} />
                )}
              </Button>
            </HStack>
            {!isRecipientCollapsed && (
              <VStack
                w="100%"
                alignSelf="start"
                alignItems="stretch"
                justifyContent="space-between"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="xl"
                bg="blackAlpha.600"
                px={4}
                py={2}
              >
                {recipientObj.map(
                  (item, index) =>
                    item.data && (
                      <DetailsTable
                        key={index}
                        isSidebar={isSidebar}
                        details={item.data}
                        rowGap="0.55rem"
                        marginTop="0"
                      />
                    )
                )}
              </VStack>
            )}
          </VStack>


          {/* On Hold */}
          {hasAnyAuthority(
              ROLES.ADMIN_ROLE,
              ROLES.FALCON_ADMIN,
              ROLES.FALCON_OPS_MANAGER
            ) &&
              (falconTransaction?.status === FALCON_STATUS.ON_HOLD ||
                falconTransaction?.error === "RECIPIENT_PAYOUT_LIMIT_EXCEEDED" ) && (
                <VStack
                  w={'100%'}
                  gap={2}
                  alignSelf={'start'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  border={'1px solid'}
                  borderColor={'whiteAlpha.300'}
                  bg={'blackAlpha.600'}
                  borderRadius="12px"
                  p={4}
                >
                  
                  {
                  (falconTransaction?.status === FALCON_STATUS.ON_HOLD ||
                    falconTransaction?.error === "RECIPIENT_PAYOUT_LIMIT_EXCEEDED" ) && (     
                    <Button
                      size={'xs'}
                      rounded="full"
                      color={'black'}
                      colorScheme="brand"
                      alignSelf={{ base: 'center' }}
                      px={6}
                      leftIcon={<MdOutlineBolt size={20} />}
                      onClick={() => {
                        handleUpdateStatusForOnHoldModal({
                          modal:true
                        })
                      }}
                      >
                      Release On Hold Transaction
                    </Button>
                      
                  )}
                </VStack>
              )}


          {/* Payout Details */}
          <VStack
            w={'100%'}
            alignSelf={'start'}
            alignItems={'stretch'}
            justifyContent={'space-between'}
            border={'1px solid'}
            borderColor={'whiteAlpha.300'}
            bg={'backGround'}
            borderRadius={'xl'}
            p={4}
          >
            <HStack justifyContent="space-between">
              <Heading fontSize="lg" color="brand.500">
                Payout Details
              </Heading>
              <HStack justifyContent="space-between" gap={2}>
                {payout.length > 0 && (
                  <Text fontSize={'xs'} color="brand.500">
                    Avlbl Payout: {payout.length}
                  </Text>
                )}
                <Button
                  size="sm"
                  bg={'blackAlpha.600'}
                  border={'1px solid'}
                  borderColor={'whiteAlpha.300'}
                  borderRadius={'xl'}
                  onClick={() => setIsPayoutCollapsed(!isPayoutCollapsed)}
                  p={0}
                  variant={'subtle'}
                >
                  {isPayoutCollapsed ? (
                    <MdOutlineKeyboardArrowDown size={'1.25rem'} />
                  ) : (
                    <MdOutlineKeyboardArrowUp size={'1.25rem'} />
                  )}
                </Button>
              </HStack>
            </HStack>
            {!isPayoutCollapsed && (
              <VStack
                overflowY="auto" // Make the area scrollable
                width="100%" // Ensure consistent width
                spacing={4} // Add spacing between payout items
              >
                {payout.length > 0 ? (
                  payout.map((payoutItem, index) => {
                    const payoutObj = getPayoutTable(payoutItem);
                    return (
                      <VStack
                        key={index}
                        w={'100%'}
                        alignSelf={'start'}
                        alignItems={'stretch'}
                        justifyContent={'space-between'}
                        border={'1px solid'}
                        borderColor={'whiteAlpha.300'}
                        borderRadius={'xl'}
                        bg={'blackAlpha.600'}
                        px={4}
                        py={2}
                      >
                        {payoutObj.map((item) => (
                          <DetailsTable
                            isSidebar={isSidebar}
                            details={item.data}
                            rowGap={'0.55rem'}
                            marginTop={'0'}
                          />
                        ))}
                      </VStack>
                    );
                  })
                ) : (
                  <HStack
                    w={'100%'}
                    alignSelf={'start'}
                    justifyContent={'center'}
                    border={'1px solid'}
                    borderColor={'whiteAlpha.300'}
                    bg={'blackAlpha.600'}
                    borderRadius="12px"
                    p={4}
                  >
                    <Text color={'whiteAlpha.800'}>No Payouts Available</Text>
                  </HStack>
                )}
              </VStack>
            )}


              {hasAnyAuthority(
                ROLES.SUPPORT_ROLE,
                ROLES.ADMIN_ROLE,
                ROLES.FALCON_ADMIN,
                ROLES.FALCON_OPS_MANAGER
              ) &&
                (falconTransaction?.status === FALCON_STATUS.COMPLETED ||
                  falconTransaction?.status === FALCON_STATUS.PROCESSING) && (
                <HStack
                  w={'100%'}
                  gap={2}
                  alignSelf={'start'}
                  justifyContent={'center'}
                  border={'1px solid'}
                  borderColor={'whiteAlpha.300'}
                  bg={'blackAlpha.600'}
                  borderRadius="12px"
                  p={4}
                >
                  <Button
                    size={'xs'}
                    rounded="full"
                    color={'black'}
                    colorScheme="brand"
                    alignSelf={{ base: 'center', md: 'start' }}
                    px={6}
                    leftIcon={<MdOutlineBolt size={20} />}
                    onClick={() => {
                      handleForceStatusSync({ isConfirmed: false });
                    }}
                  >
                    Force Sync
                  </Button>
                </HStack>
              )}

            {hasAnyAuthority(
              ROLES.FALCON_ADMIN,
              ROLES.FALCON_OPS_MANAGER,
              ROLES.SUPPORT_ROLE,
              ROLES.ADMIN_ROLE
            ) &&
              canShowTerminalStateButton && (
                <HStack
                  w={'100%'}
                  gap={2}
                  alignSelf={'start'}
                  justifyContent={'center'}
                  border={'1px solid'}
                  borderColor={'whiteAlpha.300'}
                  bg={'blackAlpha.600'}
                  borderRadius="12px"
                  p={4}
                >
                  <Button
                    size={'xs'}
                    rounded="full"
                    color={'white'}
                    colorScheme={terminalState ? 'green' : 'red'}
                    alignSelf={{ base: 'center', md: 'start' }}
                    px={6}
                    leftIcon={<MdOutlineBolt size={20} />}
                    onClick={() => {
                      handleSetTerminalState({ isConfirmed: false });
                    }}
                  >
                    {terminalState
                      ? 'Enable Re-route'
                      : 'Disable Re-route'}
                  </Button>
                </HStack>
              )}

            {hasAnyAuthority(
              ROLES.ADMIN_ROLE,
              ROLES.FALCON_ADMIN,
              ROLES.FALCON_OPS_MANAGER
            ) &&
              payout &&
              payout?.length && (
                <HStack
                  w={'100%'}
                  gap={2}
                  alignSelf={'start'}
                  justifyContent={'center'}
                  border={'1px solid'}
                  borderColor={'whiteAlpha.300'}
                  bg={'blackAlpha.600'}
                  borderRadius="12px"
                  p={4}
                >
                  <Button
                    size={'xs'}
                    rounded="full"
                    color={'black'}
                    colorScheme="red"
                    alignSelf={{ base: 'center', md: 'start' }}
                    px={6}
                    rightIcon={<MdOutlineErrorOutline size={20} />}
                    onClick={() => {
                      handleForceFailTransaction({
                        isForceFail: false
                      });
                    }}
                  >
                    Force Fail
                  </Button>
                  <Button
                    size={'xs'}
                    rounded="full"
                    color={'black'}
                    colorScheme="brand"
                    alignSelf={{ base: 'center', md: 'start' }}
                    px={6}
                    leftIcon={<MdOutlineBolt size={20} />}
                    onClick={() => {
                      handleForceProcessTransaction({
                        isForceComplete: false
                      });
                    }}
                  >
                    Force Process
                  </Button>
                </HStack>
              )}
          </VStack>

              
        </VStack>
      ) : (
        <>
          <GenericCard
            cardData={falconTransaction}
            cardType={CARD_TYPE.FALCON_SERVICE_TRANSACTION_CARD}
            maxW={'100%'}
            selected={selected}
            ActionButtons={[
              <Button
                key="details-button"
                colorScheme="brand"
                color="black"
                width="8rem"
                size="sm"
                fontSize="sm"
                mt={3}
                onClick={() => {
                  onOrderDetailsClick(falconTransaction?.transactionId);
                }}
                rightIcon={<FaArrowRightLong />}
              >
                Details
              </Button>
            ]}
          />
        </>
      )}
    </Box>
  );
};

export default FalconTransactionCard;
